import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, X, Share } from "lucide-react";
import api from "@/lib/api";
import { pushSupported, getPushStatus, enablePush, isIOS, isStandalone } from "@/lib/push";

const DISMISS_KEY = "twp_push_banner_dismissed";

export default function EnableNotificationsBanner() {
  const [mode, setMode] = useState(null); // "enable" | "ios-install" | null
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(DISMISS_KEY) === "1") return;
    // iOS Safari can only do Web Push once the app is installed to the Home Screen.
    if (isIOS() && !isStandalone() && !pushSupported()) {
      setMode("ios-install");
      return;
    }
    if (!pushSupported()) return;
    let alive = true;
    getPushStatus().then((s) => {
      if (alive && !s.enabled) setMode("enable");
    });
    return () => { alive = false; };
  }, []);

  if (!mode) return null;

  const enable = async () => {
    setBusy(true);
    try {
      await enablePush();
      setMode(null);
      localStorage.setItem(DISMISS_KEY, "1");
      await api.post("/notifications/test").catch(() => {});
      window.dispatchEvent(new Event("twp:refresh-notifications"));
      toast.success("Notifications on — you'll get alerts for assignments and status changes");
    } catch (e) {
      toast.error(e?.message || "Could not enable notifications");
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setMode(null);
  };

  const iosInstall = mode === "ios-install";

  return (
    <div
      data-testid="enable-notifications-banner"
      className="flex items-center gap-3 px-4 sm:px-6 py-3 bg-zinc-900 text-white"
    >
      <span className="h-8 w-8 shrink-0 rounded-sm bg-white/10 flex items-center justify-center">
        <Bell className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold leading-tight">
          {iosInstall ? "Get notifications on your iPhone" : "Turn on notifications"}
        </p>
        <p className="text-xs text-zinc-300 leading-tight mt-0.5">
          {iosInstall ? (
            <>Tap <Share className="inline h-3 w-3 -mt-0.5" /> Share, then <b>Add to Home Screen</b>, open TWP&nbsp;co from there and turn on notifications.</>
          ) : (
            "Get an instant alert whenever a task is assigned to you or its status changes."
          )}
        </p>
      </div>
      {!iosInstall && (
        <button
          onClick={enable}
          disabled={busy}
          data-testid="enable-notifications-btn"
          className="shrink-0 text-xs font-semibold bg-white text-zinc-900 px-3.5 py-2 rounded-sm hover:bg-zinc-200 disabled:opacity-50 transition-colors"
        >
          {busy ? "Enabling…" : "Turn on"}
        </button>
      )}
      <button
        onClick={dismiss}
        data-testid="enable-notifications-dismiss"
        aria-label="Dismiss"
        className="shrink-0 h-8 w-8 flex items-center justify-center rounded-sm text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
